import { useAppData } from '@app/providers'
import { logger } from '@shared/lib/logger'
import { useCallMe } from '@vp/contact-channels'
import { useIdentity } from '@vp/ubik-context'
import { useEffect } from 'react'
import { HELP_CENTER_FALLBACK_PATH, resolveChannel } from '../utils/channel-status.utils'
import type { Props } from '../components/page'
import type { ContactChannel } from '../components/page'

interface UsePageContentStateResult {
  channel: ContactChannel | null
  isPageLoading: boolean
  shouldHidePage: boolean
}

export const usePageContentState = (requestedChannel: Props['channel']): UsePageContentStateResult => {
  const { helpCenterUrls } = useAppData()
  const { auth, identity, isIdentityInitialized } = useIdentity()
  const { data: callMeAvailability } = useCallMe()

  const isIdentityStateSettling = !isIdentityInitialized || Boolean(auth?.getIdentity().isSignedIn) !== Boolean(identity?.isSignedIn)
  const helpCenterPath = helpCenterUrls?.path ?? HELP_CENTER_FALLBACK_PATH

  const channel = resolveChannel(requestedChannel, callMeAvailability?.plainData?.isProadvantage)
  const shouldHidePage = !channel

  const isPageLoading = isIdentityStateSettling
  const canRedirect = !isPageLoading && shouldHidePage
  const requestedChannelLabel = requestedChannel ?? 'none'
  const channelLabel = channel ?? 'none'

  useEffect(() => {
    if (!canRedirect || typeof window === 'undefined') {
      return
    }

    logger.warn('Contact page redirected because channel was unavailable', {
      action: 'contact_page_redirected',
      component: 'ContactPageContent',
      requestedChannel: requestedChannelLabel,
      resolvedChannel: channelLabel,
      redirectPath: helpCenterPath,
      isProAdvantage: callMeAvailability?.plainData?.isProadvantage ?? false,
    })

    window.location.replace(helpCenterPath)
  }, [canRedirect, channelLabel, helpCenterPath, requestedChannelLabel])

  useEffect(() => {
    if (isPageLoading) {
      return
    }

    logger.info('Contact page channel resolved', {
      action: 'contact_page_channel_resolved',
      component: 'ContactPageContent',
      requestedChannel: requestedChannelLabel,
      resolvedChannel: channelLabel,
      shouldRedirect: shouldHidePage,
      hasHelpCenterPath: Boolean(helpCenterUrls?.path),
    })
  }, [shouldHidePage, channelLabel, helpCenterUrls?.path, isPageLoading, requestedChannelLabel])

  return { channel, isPageLoading, shouldHidePage }
}
